import { useState, useEffect, useContext, createContext, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async (authUser) => {
    if (!authUser) { setProfile(null); return; }

    const valetSession = localStorage.getItem('canluxa_valet_session');
    if (valetSession) {
      try {
        const parsed = JSON.parse(valetSession);
        setProfile({ role: 'valet', ...parsed });
        return;
      } catch (e) {
        localStorage.removeItem('canluxa_valet_session');
      }
    }

    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', authUser.id)
      .maybeSingle();

    if (error) { console.error('Profile load error:', error); setProfile(null); }
    else setProfile(data);
  }, []);

  useEffect(() => {
    const valetSession = localStorage.getItem('canluxa_valet_session');
    if (valetSession) {
      try {
        const parsed = JSON.parse(valetSession);
        setProfile({ role: 'valet', ...parsed });
        setLoading(false);
        return;
      } catch (e) {
        localStorage.removeItem('canluxa_valet_session');
      }
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      loadProfile(session?.user ?? null).finally(() => setLoading(false));
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      loadProfile(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, [loadProfile]);

  const loginResident = async ({ condoCode, unitNumber, email, password }) => {
    const { data: property, error: propErr } = await supabase
      .from('properties')
      .select('id, total_units, is_active')
      .eq('condo_code', condoCode.trim().toUpperCase())
      .maybeSingle();

    if (propErr) return { error: 'Database error. Please try again.' };
    if (!property) return { error: 'Invalid Condo Code. Please check and try again.' };
    if (!property.is_active) return { error: 'This property is currently inactive.' };

    const unit = parseInt(unitNumber, 10);
    if (isNaN(unit) || unit < 1 || unit > property.total_units) {
      return { error: `Unit number must be between 1 and ${property.total_units}.` };
    }

    const { data: authData, error: authErr } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(), password,
    });
    if (authErr) return { error: mapAuthError(authErr.message) };

    const { data: prof, error: profErr } = await supabase
      .from('profiles')
      .select('role, property_id, unit_number, is_active')
      .eq('id', authData.user.id)
      .maybeSingle();

    if (profErr || !prof) return { error: 'Account not found. Contact your administrator.' };
    if (!prof.is_active) return { error: 'Your account has been deactivated.' };
    if (prof.role !== 'resident') return { error: 'This account is not a resident account.' };
    if (prof.property_id !== property.id) return { error: 'Condo Code does not match your property.' };
    if (prof.unit_number !== unit) return { error: 'Unit number does not match your account.' };

    return { success: true, role: 'resident' };
  };

  const loginValet = async ({ code }) => {
    const { data: valetCode, error } = await supabase
      .from('valet_codes')
      .select('id, code, property_id, is_active, label')
      .eq('code', code.trim().toUpperCase())
      .maybeSingle();

    if (error) return { error: 'Database error. Please try again.' };
    if (!valetCode) return { error: 'Invalid access code. Please check with your administrator.' };
    if (!valetCode.is_active) return { error: 'This access code has been deactivated.' };

    await supabase
      .from('valet_codes')
      .update({ last_used_at: new Date().toISOString() })
      .eq('id', valetCode.id);

    const valetSession = {
      type: 'valet', codeId: valetCode.id, code: valetCode.code,
      label: valetCode.label, propertyId: valetCode.property_id,
      loginAt: new Date().toISOString(),
    };
    localStorage.setItem('canluxa_valet_session', JSON.stringify(valetSession));
    setProfile({ role: 'valet', ...valetSession });

    return { success: true, role: 'valet' };
  };

  const loginHOA = async ({ email, password, hoaCode }) => {
    const { data: property, error: propErr } = await supabase
      .from('properties')
      .select('id, name, is_active')
      .eq('hoa_admin_code', hoaCode.trim().toUpperCase())
      .maybeSingle();

    if (propErr) return { error: 'Database error. Please try again.' };
    if (!property) return { error: 'Invalid HOA Admin Code. Please check and try again.' };
    if (!property.is_active) return { error: 'This property is currently inactive.' };

    const { data: authData, error: authErr } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(), password,
    });
    if (authErr) return { error: mapAuthError(authErr.message) };

    const { data: prof, error: profErr } = await supabase
      .from('profiles')
      .select('role, property_id, is_active')
      .eq('id', authData.user.id)
      .maybeSingle();

    if (profErr || !prof) return { error: 'Account not found. Contact your administrator.' };
    if (!prof.is_active) return { error: 'Your account has been deactivated.' };
    if (prof.role !== 'hoa_admin') return { error: 'This account is not an HOA Admin account.' };
    if (prof.property_id !== property.id) return { error: 'HOA code does not match your property.' };

    return { success: true, role: 'hoa_admin' };
  };

  const loginSuperAdmin = async ({ email, password }) => {
    const { data: authData, error: authErr } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(), password,
    });
    if (authErr) return { error: mapAuthError(authErr.message) };

    const { data: prof, error: profErr } = await supabase
      .from('profiles')
      .select('role, is_active')
      .eq('id', authData.user.id)
      .maybeSingle();

    if (profErr || !prof) return { error: 'Account not found.' };
    if (!prof.is_active) return { error: 'Your account has been deactivated.' };
    if (prof.role !== 'super_admin') {
      await supabase.auth.signOut();
      return { error: 'Access denied. No Super Admin privileges.' };
    }

    return { success: true, role: 'super_admin' };
  };

  const logout = async () => {
    localStorage.removeItem('canluxa_valet_session');
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
  };

  return (
    <AuthContext.Provider value={{
      user, profile, loading,
      loginResident, loginValet, loginHOA, loginSuperAdmin, logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

function mapAuthError(msg) {
  if (msg?.includes('Invalid login credentials')) return 'Incorrect email or password.';
  if (msg?.includes('Email not confirmed')) return 'Please confirm your email first.';
  if (msg?.includes('Too many requests')) return 'Too many attempts. Please wait a moment.';
  if (msg?.includes('User not found')) return 'No account found with this email.';
  return 'Login failed. Please try again.';
}






